import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backupService, BackupData } from '../services/backupService';

const BackupManager: React.FC = () => {
  const { user } = useAuth();
  const [backups, setBackups] = useState<BackupData[]>([]);

  useEffect(() => {
    if (user?.id) {
      const userBackups = backupService.getUserBackups(user.id);
      setBackups(userBackups.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ));
    }
  }, [user]);

  const handleRestore = (backup: BackupData) => {
    if (window.confirm(`Bu yedeği geri yüklemek istediğinizden emin misiniz?\nTarih: ${new Date(backup.timestamp).toLocaleString('tr-TR')}\n\nBu işlem mevcut verilerinizin üzerine yazacaktır!`)) {
      backupService.restoreFromBackup(backup);
      window.location.reload(); // Sayfayı yenile
    }
  };

  const handleDownload = (backup: BackupData) => {
    backupService.downloadBackup(backup);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && user?.id) {
      backupService.uploadBackup(file, user.id)
        .then(backup => {
          if (window.confirm(`Yüklenen yedek geri yüklensin mi?\nTarih: ${new Date(backup.timestamp).toLocaleString('tr-TR')}`)) {
            backupService.restoreFromBackup(backup);
            window.location.reload();
          }
        })
        .catch(error => {
          alert('Yedek yüklenirken hata oluştu: ' + error.message);
        });
    }
  };

  const handleDelete = (backup: BackupData) => {
    if (window.confirm('Bu yedeği silmek istediğinizden emin misiniz?')) {
      const allBackups = backupService.getBackups();
      const filteredBackups = allBackups.filter(b => b.timestamp !== backup.timestamp);
      localStorage.setItem('finance_app_backups', JSON.stringify(filteredBackups));
      
      const userBackups = backupService.getUserBackups(user?.id || 0);
      setBackups(userBackups.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ));
    }
  };

  const handleCreateManualBackup = async () => {
    if (user?.id) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/user-data', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) throw new Error('Veri alınamadı');
        
        const data = await response.json();
        const portfolio = data.portfolio || [];
        const payments = data.payments || [];
        const sellTransactions = data.sellTransactions || [];
        
        backupService.createBackup(user.id, user.email, portfolio, payments, sellTransactions);
        
        // Yedek listesini güncelle
        const userBackups = backupService.getUserBackups(user.id);
        setBackups(userBackups.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ));
        
        alert('Manuel yedekleme oluşturuldu!');
      } catch (error: any) {
        alert('Yedekleme oluşturulurken hata oluştu: ' + error.message);
      }
    }
  };

  if (!user) return null;

  return (
    <div className="backup-manager">
      <div className="backup-header">
        <h3>📦 Yedekleme Yönetimi</h3>
        <div className="backup-actions">
          <button onClick={handleCreateManualBackup} className="backup-button create">
            💾 Manuel Yedekle
          </button>
          <label className="backup-button upload">
            📁 Yedek Yükle
            <input
              type="file"
              accept=".json"
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      <div className="backup-info">
        <p>📊 <strong>Toplam Yedek:</strong> {backups.length} adet</p>
        <p>🔄 <strong>Otomatik Yedekleme:</strong> Her veri değişikliğinde</p>
        <p>🗂️ <strong>Yedek Saklama:</strong> Son 10 yedek</p>
      </div>

      {backups.length === 0 ? (
        <div className="no-backups">
          <p>📭 Henüz yedek bulunmuyor.</p>
          <p>Veri eklediğinizde otomatik yedeklemeler oluşturulacaktır.</p>
        </div>
      ) : (
        <div className="backup-list">
          {backups.map((backup, index) => (
            <div key={backup.timestamp} className="backup-item">
              <div className="backup-info">
                <div className="backup-date">
                  📅 {new Date(backup.timestamp).toLocaleString('tr-TR')}
                </div>
                <div className="backup-details">
                  📊 Portföy: {backup.portfolio.length} | 
                  💳 Ödemeler: {backup.payments.length} | 
                  💰 Satışlar: {backup.sellTransactions.length}
                </div>
                <div className="backup-user">
                  👤 {backup.userEmail}
                </div>
              </div>
              <div className="backup-actions">
                <button 
                  onClick={() => handleDownload(backup)}
                  className="backup-button download"
                  title="İndir"
                >
                  📥
                </button>
                <button 
                  onClick={() => handleRestore(backup)}
                  className="backup-button restore"
                  title="Geri Yükle"
                >
                  🔄
                </button>
                <button 
                  onClick={() => handleDelete(backup)}
                  className="backup-button delete"
                  title="Sil"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="backup-tips">
        <h4>💡 İpuçları:</h4>
        <ul>
          <li>🔄 Verileriniz her değiştiğinde otomatik yedeklenir</li>
          <li>📥 Yedeklerinizi JSON dosyası olarak indirebilirsiniz</li>
          <li>📁 JSON dosyalarından yedek yükleyebilirsiniz</li>
          <li>🗑️ Eski yedekleri silerek yer açabilirsiniz</li>
          <li>🛡️ Düzenli yedekleme veri kaybını önler</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;
